import { useState, type FormEvent } from 'react';
import { useUploadFilesToContractMutation } from '@shared/api';
import { ACCEPT_CONTRACT_SET } from '@shared/lib/fileAccept';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Modal } from '@shared/ui/Modal';
import { MultiFileList } from '@shared/ui/MultiFileList';
import {
  AttachOnlyCheckbox,
  AttachOnlyDescription,
  AttachOnlyLabel,
  ErrorBanner,
  UploadModalActionRow,
} from './styles';

interface UploadFilesModalProps {
  open: boolean;
  contractId: string;
  canWrite: boolean;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

export function UploadFilesModal({
  open,
  contractId,
  canWrite,
  onClose,
  onSuccess,
}: UploadFilesModalProps) {
  const [uploadFiles, { isLoading: isUploading }] = useUploadFilesToContractMutation();
  const [picked, setPicked] = useState<File[]>([]);
  const [attachOnly, setAttachOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function resetModal() {
    setPicked([]);
    setAttachOnly(false);
    setError(null);
  }

  function handleClose() {
    resetModal();
    onClose();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (picked.length === 0) return;
    setError(null);
    const form = new FormData();
    for (const f of picked) {
      form.append('file', f);
    }
    if (attachOnly) {
      form.append('no_analysis', '1');
    }
    try {
      const res = await uploadFiles({ contractId, form }).unwrap();
      if (attachOnly || res.attached) {
        const n = res.added ?? picked.length;
        onSuccess(
          `Приложено ${n} файл(ов) к комплекту без анализа. Риски договора не пересчитывались — ` +
            'документ сохранён и доступен для скачивания.',
        );
      } else {
        onSuccess(
          `Документы добавлены к комплекту (версия v${res.version_no}), запущен повторный анализ всего договора.`,
        );
      }
      handleClose();
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось загрузить файлы. Попробуйте ещё раз.';
      setError(msg);
    }
  }

  return (
    <Modal open={open} title="Догрузить документы к договору" onClose={handleClose}>
      <form onSubmit={onSubmit}>
        <FileDropZone
          multiple
          accept={ACCEPT_CONTRACT_SET}
          hint="Добавит выбранные документы к комплекту и переанализирует весь договор"
          onFiles={(files) =>
            setPicked((prev) => {
              const seen = new Set(prev.map((f) => `${f.name}::${f.size}`));
              const next = [...prev];
              for (const f of files) {
                const key = `${f.name}::${f.size}`;
                if (!seen.has(key)) {
                  next.push(f);
                  seen.add(key);
                }
              }
              return next;
            })
          }
        />
        <MultiFileList
          files={picked}
          onRemove={(idx) => setPicked((prev) => prev.filter((_, i) => i !== idx))}
        />

        <AttachOnlyLabel>
          <AttachOnlyCheckbox
            type="checkbox"
            checked={attachOnly}
            onChange={(e) => setAttachOnly(e.target.checked)}
          />
          <span>
            <strong>Приложить без переанализа</strong>
            <br />
            <AttachOnlyDescription>
              Файл добавится к комплекту и будет доступен для скачивания, но LLM-экспертиза
              не запустится — риски договора останутся как есть. Для готовых документов
              (наш протокол разногласий, скан подписанного), которые разбирать уже не нужно.
            </AttachOnlyDescription>
          </span>
        </AttachOnlyLabel>

        {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

        <UploadModalActionRow>
          <Button type="button" $variant="ghost" onClick={handleClose}>
            Отмена
          </Button>
          <Button
            type="submit"
            disabled={isUploading || picked.length === 0 || !canWrite}
            title={!canWrite ? 'Только чтение' : undefined}
          >
            {isUploading
              ? 'Загрузка…'
              : attachOnly
                ? `Приложить ${picked.length || ''} без анализа`
                : `Добавить ${picked.length || ''} к комплекту и переанализировать`}
          </Button>
        </UploadModalActionRow>
      </form>
    </Modal>
  );
}
